import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Image as ImageIcon, ZoomIn, Maximize2, Share2, Download, Layers } from "lucide-react";
import { Badge } from "../ui/badge";

const imageTabs = ["Periapical", "Bitewing", "Panoramic", "CBCT"];

const thumbnails = [
  { label: "#14 PA", date: "Feb 15" },
  { label: "#3 PA", date: "Jan 10" },
  { label: "BWR", date: "Jan 10" },
  { label: "BWL", date: "Jan 10" },
  { label: "Pano", date: "Dec 5" },
  { label: "#30 PA", date: "Nov 20" },
];

const PMSImagingSlide = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeThumb, setActiveThumb] = useState(0);

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setActiveThumb(0);
        setActiveTab(0);
        await new Promise((r) => setTimeout(r, 4000));
        setActiveThumb(2);
        setActiveTab(1);
        await new Promise((r) => setTimeout(r, 4000));
        setActiveThumb(4);
        setActiveTab(2);
        await new Promise((r) => setTimeout(r, 4000));
      }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute top-[50%] right-[30%] w-[300px] h-[300px] bg-sky-100 rounded-full blur-[80px] opacity-40" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <ImageIcon className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Advanced{" "}
              <span className="text-blue-600">Digital Imaging</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Full imaging suite with DICOM support, measurement tools, and
              seamless sensor integration. Compare images side-by-side and
              present findings to patients.
            </p>
          </motion.div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              "DICOM Support",
              "Measurements",
              "Side-by-Side",
              "Sensor Integration",
              "Patient Education",
              "Cloud-Based",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">DICOM</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Full Compliance
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-5 h-5 text-blue-600" />
                <span className="text-3xl font-bold text-blue-600">4K</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Image Quality
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Imaging Viewer Mockup */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <ImageIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Imaging Viewer</h3>
                  <p className="text-xs text-slate-500">Amanda Chen &bull; 28 images</p>
                </div>
              </div>
              <div className="flex gap-2">
                {[ZoomIn, Maximize2, Share2, Download].map((Icon, i) => (
                  <button key={i} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                    <Icon className="w-4 h-4 text-slate-500" />
                  </button>
                ))}
              </div>
            </div>

            {/* Image Type Tabs */}
            <div className="flex gap-2 px-6 py-3 border-b border-slate-100">
              {imageTabs.map((tab, i) => (
                <motion.div
                  key={tab}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    activeTab === i
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  animate={{ scale: activeTab === i ? 1.05 : 1 }}
                >
                  {tab}
                </motion.div>
              ))}
            </div>

            {/* Main Image Display */}
            <div className="flex-1 p-4 flex gap-3 overflow-hidden">
              {/* Main viewer */}
              <div className="flex-1 bg-slate-900 rounded-xl overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />

                {/* Simulated X-Ray visualization */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-[20%] left-[30%] w-40 h-40 bg-slate-600 rounded-full blur-3xl" />
                  <div className="absolute bottom-[20%] right-[25%] w-48 h-48 bg-slate-500 rounded-full blur-3xl" />
                </div>

                {/* Center content */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <ImageIcon className="w-14 h-14 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm font-mono">
                      {imageTabs[activeTab]} X-RAY
                    </p>
                    <p className="text-slate-500 text-xs font-mono mt-1">
                      {thumbnails[activeThumb]?.label}
                    </p>
                  </div>
                </div>

                {/* Measurement overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="absolute bottom-12 left-6 bg-blue-500/90 text-white px-2 py-1 rounded text-xs font-bold"
                >
                  12.4 mm
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="absolute bottom-[55px] left-6 w-24 h-0.5 bg-blue-400"
                />

                {/* Image info */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <div className="bg-black/50 backdrop-blur px-3 py-1 rounded-lg text-white text-[10px]">
                    <span className="opacity-75">Date:</span> <span className="font-bold">{thumbnails[activeThumb]?.date}, 2026</span>
                  </div>
                  <div className="bg-black/50 backdrop-blur px-3 py-1 rounded-lg text-white text-[10px]">
                    <span className="opacity-75">kVp:</span> <span className="font-bold">70</span> |{" "}
                    <span className="opacity-75">mA:</span> <span className="font-bold">8</span>
                  </div>
                </div>
              </div>

              {/* Thumbnail strip (vertical) */}
              <div className="w-16 flex flex-col gap-2 overflow-y-auto">
                {thumbnails.map((thumb, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.3 }}
                    className={`aspect-square rounded-lg cursor-pointer border-2 transition-all flex flex-col items-center justify-center gap-0.5 ${
                      activeThumb === i
                        ? "bg-gradient-to-br from-slate-700 to-slate-800 border-blue-400 shadow-md"
                        : "bg-gradient-to-br from-slate-200 to-slate-300 border-slate-300 hover:border-blue-300"
                    }`}
                  >
                    <ImageIcon className={`w-4 h-4 ${activeThumb === i ? "text-blue-400" : "text-slate-500"}`} />
                    <span className={`text-[8px] font-bold ${activeThumb === i ? "text-blue-300" : "text-slate-500"}`}>
                      {thumb.label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Bottom bar */}
            <div className="h-10 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6">
              <div className="flex items-center gap-2 text-xs text-blue-600 font-bold">
                <ImageIcon className="w-4 h-4" />
                <span>Cloud-Based Imaging</span>
              </div>
              <div className="text-xs text-slate-500 font-medium">
                <span className="text-blue-600 font-bold">DICOM</span> compliant
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSImagingSlide;
